import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select, Textarea, SearchInput } from "../components/ui/Fields";
import { Avatar } from "../components/ui/Avatar";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError, uploadAgencyPicture } from "../api/client";
import { useOffsetList } from "../hooks/useOffsetList";
import type { AgencyRow, AgencyUserLookup } from "../api/types";

const statusTone = (s: string) => (s === "active" ? "success" : s === "suspended" ? "warn" : "danger");

/**
 * Round 54 — Agencies. Create (founder by User ID, auto 7-digit Agency ID,
 * country copied from the founder's profile, admin-uploaded picture),
 * edit, change picture / founder, suspend / reactivate, members. All
 * mutations are super_admin-only on the Worker and audited by the database.
 */
export function AgenciesPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload, pageSize } = useOffsetList<AgencyRow>("/admin/agencies", { q: q.trim(), status });
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<AgencyRow | null>(null);

  const columns: Column<AgencyRow>[] = [
    { header: "Agency", render: (r) => (
      <div className="flex items-center gap-2">
        <Avatar src={r.picture_url} className="h-8 w-8 rounded-lg object-cover" />
        <div><p className="font-medium">{r.name} <span className="text-xs text-ink-faint">[{r.tag}]</span></p><p className="text-xs text-ink-faint">ID {r.agency_id}</p></div>
      </div>) },
    { header: "Founder", render: (r) => <span>{r.founder_name} <span className="text-xs text-ink-faint">#{r.founder_public_id}</span></span> },
    { header: "Country", render: (r) => r.country ?? "—" },
    { header: "Members", render: (r) => r.member_count },
    { header: "Hosts", render: (r) => r.host_count },
    { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
    { header: "Created", render: (r) => new Date(r.created_at).toLocaleDateString() },
    { header: "", render: (r) => <Button size="sm" variant="secondary" onClick={() => setManaging(r)}>Manage</Button> },
  ];

  return (
    <AppShell title="Agencies">
      <div className="mb-4 flex flex-wrap items-end gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="Agency ID, name, tag, founder name or ID..." />
        <div className="w-40"><Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option><option value="disabled">Disabled</option><option value="removed">Removed</option>
        </Select></div>
        <Button className="ml-auto" onClick={() => setCreating(true)}>Create Agency</Button>
      </div>
      <DataTable columns={columns} rows={rows} keyField={(r) => r.id} isLoading={isLoading} error={error} onRetry={reload}
        emptyTitle="No agencies yet" pagination={{ page, pageSize, hasNextPage, onPageChange: setPage }} />
      {creating && <CreateAgencyModal onClose={() => setCreating(false)} onDone={() => { setCreating(false); reload(); }} />}
      {managing && <ManageAgencyModal agency={managing} onClose={() => setManaging(null)} onChanged={reload} />}
    </AppShell>
  );
}

function PictureField({ url, setUrl }: { url: string; setUrl: (u: string) => void }) {
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  async function pick(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    try { setUrl(await uploadAgencyPicture(file)); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Upload failed", "error"); }
    finally { setBusy(false); }
  }
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-ink-muted">Agency picture (PNG / JPEG / WebP, max 2 MB)</label>
      <div className="flex items-center gap-3">
        {url ? <img src={url} alt="" className="h-16 w-16 rounded-xl object-cover" /> : <div className="h-16 w-16 rounded-xl bg-surface-raised" />}
        <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} className="text-xs text-ink-muted" />
        {busy && <span className="text-xs text-ink-faint">Uploading…</span>}
      </div>
    </div>
  );
}

function CreateAgencyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [uid, setUid] = useState("");
  const [founder, setFounder] = useState<AgencyUserLookup | null>(null);
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [pic, setPic] = useState("");
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  async function lookup() {
    setFounder(null);
    try { setFounder(await apiRequest<AgencyUserLookup>("/admin/agencies/lookup-user", { params: { user_id: uid.trim() } })); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Lookup failed", "error"); }
  }
  async function create() {
    if (!founder) return;
    setBusy(true);
    try {
      const r = await apiRequest<{ agencyId?: string; agency_id?: string }>("/admin/agencies", { method: "POST", body: { founderUserId: String(founder.user_id), name, tag, pictureUrl: pic } });
      showToast(`Agency created (ID ${r.agencyId ?? r.agency_id ?? "?"})`, "success");
      onDone();
    } catch (e) { showToast(e instanceof ApiError ? e.message : "Could not create agency", "error"); }
    finally { setBusy(false); }
  }
  const blocked = founder?.roles?.agency ? `Already in agency ${founder.roles.agency.name}` : null;
  return (
    <Modal isOpen onClose={onClose} title="Create Agency" footer={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button onClick={create} isLoading={busy} disabled={!founder || !!blocked || name.trim().length < 2 || tag.trim().length < 2 || !pic}>Create</Button></>}>
      <div className="space-y-4">
        <div className="flex items-end gap-2">
          <Input label="Founder — User ID" value={uid} onChange={(e) => setUid(e.target.value)} placeholder="e.g. 10234" />
          <Button variant="secondary" onClick={lookup} disabled={!/^\d+$/.test(uid.trim())}>Find</Button>
        </div>
        {founder && (
          <div className="flex items-center gap-3 rounded-lg border border-surface-border p-3">
            <Avatar src={founder.avatar_url} className="h-10 w-10 rounded-full object-cover" />
            <div className="text-sm"><p className="font-medium">{founder.username}</p><p className="text-xs text-ink-faint">ID {founder.user_id} · Country {founder.country ?? "—"} (copied to the agency)</p></div>
            {blocked && <Badge tone="danger">{blocked}</Badge>}
          </div>
        )}
        <Input label="Agency name (2–40, unique)" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
        <Input label="Agency tag (2–8 letters/digits, unique)" value={tag} onChange={(e) => setTag(e.target.value)} maxLength={8} />
        <PictureField url={pic} setUrl={setPic} />
        <p className="text-xs text-ink-faint">A unique 7-digit Agency ID is generated automatically and can never be changed.</p>
      </div>
    </Modal>
  );
}

function ManageAgencyModal({ agency, onClose, onChanged }: { agency: AgencyRow; onClose: () => void; onChanged: () => void }) {
  const { showToast } = useToast();
  const [name, setName] = useState(agency.name);
  const [tag, setTag] = useState(agency.tag);
  const [country, setCountry] = useState(agency.country ?? "");
  const [desc, setDesc] = useState(agency.description ?? "");
  const [reason, setReason] = useState("");
  const [pic, setPic] = useState(agency.picture_url ?? "");
  const [newFounder, setNewFounder] = useState("");
  const [memberUid, setMemberUid] = useState("");
  const [members, setMembers] = useState<Array<Record<string, unknown>> | null>(null);
  const [busy, setBusy] = useState(false);

  async function act(path: string, body: Record<string, unknown>, ok: string) {
    setBusy(true);
    try { await apiRequest(path, { method: "POST", body: { agencyId: agency.agency_id, ...body } }); showToast(ok, "success"); onChanged(); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Action failed", "error"); }
    finally { setBusy(false); }
  }
  async function loadMembers() {
    try { setMembers(await apiRequest<Array<Record<string, unknown>>>("/admin/agencies/members", { params: { agency_id: agency.agency_id } })); }
    catch (e) { showToast(e instanceof ApiError ? e.message : "Could not load members", "error"); }
  }
  return (
    <Modal isOpen onClose={onClose} title={`${agency.name} · ID ${agency.agency_id}`} footer={<Button variant="ghost" onClick={onClose}>Close</Button>}>
      <div className="space-y-5">
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Edit details</h3>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={40} />
          <Input label="Tag" value={tag} onChange={(e) => setTag(e.target.value)} maxLength={8} />
          <Input label="Country" value={country} onChange={(e) => setCountry(e.target.value)} />
          <Textarea label="Description" value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={300} />
          <Input label="Reason (audit)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <Button isLoading={busy} onClick={() => act("/admin/agencies/update", { name, tag, country, description: desc, reason }, "Agency updated")}>Save changes</Button>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Agency picture (admin only)</h3>
          <PictureField url={pic} setUrl={setPic} />
          <Button isLoading={busy} disabled={!pic || pic === agency.picture_url} onClick={() => act("/admin/agencies/picture", { pictureUrl: pic }, "Picture changed")}>Save picture</Button>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Status — currently <Badge tone={statusTone(agency.status)}>{agency.status}</Badge></h3>
          <div className="flex flex-wrap gap-2">
            {["active", "suspended", "disabled", "removed"].filter((s) => s !== agency.status).map((s) => (
              <Button key={s} size="sm" variant={s === "active" ? "primary" : "danger"} isLoading={busy}
                onClick={() => act("/admin/agencies/status", { status: s, reason }, `Agency ${s}`)}>{s === "active" ? "Reactivate" : `Set ${s}`}</Button>))}
          </div>
          <p className="text-xs text-ink-faint">Members are never deleted and historical stats are always kept. Use “Reason” above for the audit trail.</p>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Change founder</h3>
          <div className="flex items-end gap-2"><Input label="New founder User ID" value={newFounder} onChange={(e) => setNewFounder(e.target.value)} />
            <Button variant="danger" isLoading={busy} disabled={!/^\d+$/.test(newFounder.trim())} onClick={() => act("/admin/agencies/founder", { newFounderUserId: newFounder.trim(), reason }, "Founder changed")}>Change</Button></div>
        </section>
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Members</h3>
          <div className="flex items-end gap-2"><Input label="User ID" value={memberUid} onChange={(e) => setMemberUid(e.target.value)} />
            <Button isLoading={busy} disabled={!/^\d+$/.test(memberUid.trim())} onClick={() => act("/admin/agencies/member", { userId: memberUid.trim(), action: "add" }, "Member added")}>Add</Button>
            <Button variant="danger" isLoading={busy} disabled={!/^\d+$/.test(memberUid.trim())} onClick={() => act("/admin/agencies/member", { userId: memberUid.trim(), action: "remove" }, "Member removed")}>Remove</Button></div>
          <Button size="sm" variant="secondary" onClick={loadMembers}>{members ? "Reload members" : "Show members"}</Button>
          {members && <ul className="max-h-48 divide-y divide-surface-border overflow-auto rounded-lg border border-surface-border text-xs">
            {members.map((m, i) => <li key={i} className="flex justify-between px-3 py-2"><span>{String(m.username)} <span className="text-ink-faint">#{String(m.publicId)}</span></span><span className="text-ink-faint">{String(m.role)}{m.hostStatus ? ` · host ${String(m.hostStatus)}` : ""}</span></li>)}
          </ul>}
        </section>
      </div>
    </Modal>
  );
}
